import React, { useState, useEffect } from 'react';
import {
  FiMail, FiRefreshCw, FiSave, FiEye, FiEyeOff, FiCheck,
  FiAlertCircle, FiSend, FiCheckCircle, FiZap, FiX,
} from 'react-icons/fi';
import { getAdminSmtpConfig, updateAdminSmtpConfig, testAdminSmtpConfig } from '../../api/admin';
import type { AdminSmtpConfig } from '../../api/admin';

/* ── Présets fournisseurs ─────────────────────────────────────────────────────── */
const PRESETS = [
  { label: 'Gmail',       host: 'smtp.gmail.com',       port: '587', secure: false,
    hint: 'Utiliser un "App Password" (pas le MDP Gmail)' },
  { label: 'Office 365',  host: 'smtp.office365.com',   port: '587', secure: false,
    hint: 'Utiliser l\'email + mot de passe Office 365' },
  { label: 'SendGrid',    host: 'smtp.sendgrid.net',     port: '587', secure: false,
    hint: 'Login: "apikey" / Password: votre API key SendGrid' },
  { label: 'Mailgun',     host: 'smtp.mailgun.org',      port: '587', secure: false,
    hint: 'Login: postmaster@votre-domaine.mailgun.org' },
  { label: 'OVH',         host: 'ssl0.ovh.net',          port: '465', secure: true,
    hint: 'Email + MDP OVH, port 465 avec SSL' },
  { label: 'Amazon SES',  host: 'email-smtp.eu-west-1.amazonaws.com', port: '587', secure: false,
    hint: 'Utiliser les credentials SMTP IAM SES' },
];

/* ── composant ───────────────────────────────────────────────────────────────── */
const AdminSmtpConfigPage: React.FC = () => {
  const [config,   setConfig]   = useState<AdminSmtpConfig | null>(null);
  const [form,     setForm]     = useState<any | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [success,  setSuccess]  = useState(false);
  const [error,    setError]    = useState<string | null>(null);
  const [showPass, setShowPass] = useState(false);
  // Test email
  const [testEmail,  setTestEmail]  = useState('');
  const [testing,    setTesting]    = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const [showTest,   setShowTest]   = useState(false);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const { config: data } = await getAdminSmtpConfig();
      setConfig(data);
      if (data) setForm({
        host: data.host, port: String(data.port), secure: data.secure,
        user_email: data.user_email, password: '',
        from_email: data.from_email, from_name: data.from_name,
        is_active: data.is_active,
      });
    } catch { setError('Impossible de charger la configuration SMTP'); }
    finally  { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const set = (key: string) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f: any) => f ? { ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value } : f);

  const applyPreset = (p: typeof PRESETS[0]) => {
    setForm((f: any) => f ? { ...f, host: p.host, port: p.port, secure: p.secure } : f);
  };

  const save = async () => {
    if (!form) return;
    setSaving(true); setError(null); setSuccess(false);
    try {
      const payload: any = {
        host: form.host, port: parseInt(form.port, 10) || 587,
        secure: form.secure, user_email: form.user_email,
        from_email: form.from_email, from_name: form.from_name,
        is_active: form.is_active,
      };
      if (form.password) payload.password = form.password;
      const { config: updated } = await updateAdminSmtpConfig(payload);
      setConfig(updated);
      setForm({ ...payload, port: String(payload.port), password: '' });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch { setError('Échec de la sauvegarde'); }
    finally  { setSaving(false); }
  };

  const sendTest = async () => {
    if (!testEmail) return;
    setTesting(true); setTestResult(null);
    try {
      const res = await testAdminSmtpConfig(testEmail);
      setTestResult(res);
    } catch { setTestResult({ success: false, message: 'Erreur réseau' }); }
    finally  { setTesting(false); }
  };

  const isConfigured = !!(config?.host && config?.user_email && config?.has_pass);
  const activePreset = PRESETS.find(p => form?.host === p.host);

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* ── Header ── */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Configuration SMTP</h2>
          <p className="text-sm text-gray-500 mt-0.5">Serveur email sortant — notifications et invitations</p>
        </div>
        <div className="flex gap-2">
          {isConfigured && (
            <button onClick={() => { setShowTest(true); setTestResult(null); }}
              className="flex items-center gap-2 px-3 py-1.5 border border-pink-300 text-pink-700 rounded-lg text-sm hover:bg-pink-50 transition">
              <FiSend size={14} /> Envoyer un test
            </button>
          )}
          <button onClick={load}
            className="flex items-center gap-2 px-3 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 transition">
            <FiRefreshCw size={14} className={loading ? 'animate-spin' : ''} /> Actualiser
          </button>
        </div>
      </div>

      {/* ── Statut global ── */}
      {!loading && config && (
        <div className={`mb-5 p-3 rounded-xl border flex items-center gap-3 ${
          isConfigured ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}>
          {isConfigured ? (
            <>
              <FiCheckCircle className="text-green-600 flex-shrink-0" size={18} />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-800">
                  SMTP configuré — <strong>{config.host}:{config.port}</strong>
                  {config.test_status === 'ok'   && <span className="ml-2 text-xs text-green-600">✅ Dernier test réussi</span>}
                  {config.test_status === 'error' && <span className="ml-2 text-xs text-red-600">❌ Dernier test échoué</span>}
                </p>
              </div>
            </>
          ) : (
            <>
              <FiAlertCircle className="text-orange-600 flex-shrink-0" size={18} />
              <p className="text-sm font-medium text-orange-800">
                Configuration incomplète — les emails ne seront pas envoyés
              </p>
            </>
          )}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
          <FiAlertCircle size={14} /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <div className="w-5 h-5 border-2 border-pink-500 border-t-transparent rounded-full animate-spin" />
          Chargement…
        </div>
      ) : form ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* ── Formulaire principal ── */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
              <h3 className="font-semibold text-gray-800 text-sm">Paramètres serveur</h3>
            </div>
            <div className="px-5 py-4 space-y-4">

              {/* Présets */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-2 flex items-center gap-1.5">
                  <FiZap size={12} /> Présets rapides
                </label>
                <div className="flex flex-wrap gap-2">
                  {PRESETS.map(p => (
                    <button key={p.label} onClick={() => applyPreset(p)}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium border transition ${
                        activePreset?.label === p.label
                          ? 'bg-pink-100 border-pink-400 text-pink-700'
                          : 'bg-gray-50 border-gray-300 text-gray-600 hover:border-gray-400'
                      }`}>
                      {p.label}
                    </button>
                  ))}
                </div>
                {activePreset?.hint && (
                  <p className="mt-1.5 text-xs text-amber-700 bg-amber-50 px-2.5 py-1.5 rounded border border-amber-200">
                    💡 {activePreset.hint}
                  </p>
                )}
              </div>

              {/* Host + Port */}
              <div className="grid grid-cols-3 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Hôte SMTP</label>
                  <input type="text" value={form.host} onChange={set('host')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Port</label>
                  <input type="number" value={form.port} onChange={set('port')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="587" />
                </div>
              </div>

              {/* TLS */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="smtp-secure" checked={form.secure} onChange={set('secure')}
                  className="h-4 w-4 accent-pink-600 border-gray-300 rounded" />
                <label htmlFor="smtp-secure" className="text-sm text-gray-700">
                  TLS/SSL <span className="text-gray-400 text-xs">(port 465)</span>
                </label>
              </div>

              {/* Email utilisateur */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Email utilisateur (login)</label>
                <input type="email" value={form.user_email} onChange={set('user_email')}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                  placeholder="no-reply@geniac.io" />
              </div>

              {/* Mot de passe */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Mot de passe SMTP
                  {config?.has_pass && !form.password && (
                    <span className="ml-2 text-green-600 font-normal text-xs">
                      <FiCheck size={10} className="inline" /> configuré — laisser vide pour conserver
                    </span>
                  )}
                </label>
                <div className="relative">
                  <input type={showPass ? 'text' : 'password'} value={form.password} onChange={set('password')}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder={config?.has_pass ? '••••••••' : 'Mot de passe'} />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                    {showPass ? <FiEyeOff size={16} /> : <FiEye size={16} />}
                  </button>
                </div>
              </div>

              {/* From */}
              <div className="grid grid-cols-2 gap-3 pt-1 border-t border-gray-100">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email expéditeur</label>
                  <input type="email" value={form.from_email} onChange={set('from_email')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="no-reply@geniac.io" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Nom expéditeur</label>
                  <input type="text" value={form.from_name} onChange={set('from_name')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-pink-500"
                    placeholder="GenIaC" />
                </div>
              </div>

              {/* Actif */}
              <div className="flex items-center gap-2">
                <input type="checkbox" id="smtp-active" checked={form.is_active} onChange={set('is_active')}
                  className="h-4 w-4 accent-pink-600 border-gray-300 rounded" />
                <label htmlFor="smtp-active" className="text-sm text-gray-700">Activer l'envoi d'emails</label>
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                {success
                  ? <span className="text-sm text-green-600 flex items-center gap-1"><FiCheckCircle size={14} /> Sauvegardé</span>
                  : <span />
                }
                <button onClick={save} disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-pink-600 hover:bg-pink-700 text-white text-sm rounded-lg transition disabled:opacity-50">
                  {saving ? <FiRefreshCw size={14} className="animate-spin" /> : <FiSave size={14} />}
                  Enregistrer
                </button>
              </div>
            </div>
          </div>

          {/* ── Panneau latéral ── */}
          <div className="space-y-4">
            {/* Aperçu From */}
            {(form.from_email || form.from_name) && (
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Aperçu expéditeur</p>
                <div className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-pink-700 font-bold text-xs">
                    {(form.from_name || form.from_email)[0]?.toUpperCase()}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{form.from_name || '—'}</p>
                    <p className="text-xs text-gray-500">{form.from_email || form.user_email}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Info */}
            <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 text-xs text-gray-600 space-y-2">
              <p className="font-semibold text-gray-700 flex items-center gap-1.5"><FiMail size={13} /> À savoir</p>
              <p>Stocké chiffré AES-256. S'applique <strong>immédiatement</strong> sans redémarrage.</p>
              <p className="text-gray-400">Port 587 = STARTTLS · Port 465 = SSL/TLS direct</p>
            </div>

            {/* Dernier test */}
            {config?.test_status && (
              <div className={`rounded-xl border p-4 text-xs ${
                config.test_status === 'ok'
                  ? 'bg-green-50 border-green-200 text-green-700'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}>
                <p className="font-semibold">
                  {config.test_status === 'ok' ? '✅ Dernier test réussi' : '❌ Dernier test échoué'}
                </p>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="text-center py-12 text-gray-400">Aucune configuration SMTP trouvée.</div>
      )}

      {/* ── Modal test email ── */}
      {showTest && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-bold text-gray-900 flex items-center gap-2">
                <FiSend size={16} className="text-pink-600" /> Envoyer un email de test
              </h3>
              <button onClick={() => setShowTest(false)} className="text-gray-400 hover:text-gray-600">
                <FiX size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <p className="text-sm text-gray-600">
                Un email HTML sera envoyé depuis <strong>{form?.from_email || form?.user_email}</strong> pour vérifier que la configuration SMTP fonctionne.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Adresse email destinataire</label>
                <input type="email" value={testEmail}
                  onChange={e => setTestEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && sendTest()}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                  placeholder="vous@exemple.com" />
              </div>
              {testResult && (
                <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm border ${
                  testResult.success
                    ? 'bg-green-50 border-green-200 text-green-700'
                    : 'bg-red-50 border-red-200 text-red-700'
                }`}>
                  {testResult.success ? <FiCheckCircle size={15} /> : <FiAlertCircle size={15} />}
                  {testResult.message}
                </div>
              )}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button onClick={() => setShowTest(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                Fermer
              </button>
              <button onClick={sendTest} disabled={testing || !testEmail}
                className="flex items-center gap-2 px-4 py-2 bg-pink-600 text-white rounded-lg text-sm font-medium hover:bg-pink-700 transition disabled:opacity-50">
                {testing
                  ? <><div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" /> Envoi…</>
                  : <><FiSend size={14} /> Envoyer</>
                }
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSmtpConfigPage;
